import React, { useContext, useEffect } from 'react';
import { Box, Stack, Paper, Tabs, Tab } from '@mui/material';
import { Table, TableBody, TableContainer, TableHead, TableRow, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid';
import { AccordionSummary, AccordionDetails, Accordion } from '../shared/components/accordion';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import StyledTableRow from '../shared/components/styledTableRow';
import StyledTableCell from '../shared/components/styledTableCell';
import { TabPanel, a11yProps } from '../shared/components/tabPanel';
import ConfirmDialog from '../shared/components/confirmDialog';
import { FormMode } from '../utils/extensions/constantEx'
import EditIcon from '@mui/icons-material/Edit';
import { HomeContext } from '../utils/contexts/homeContext';
import { NurseFormContext } from '../utils/contexts/nurseFormContext';
import {
    NurseAdvancePaySearchAPIUrl, GetNoToCurrencyFormat, GetDayJSValueFromUTCDate, NurseAdvanceReturnPaySearchAPIUrl,
    NurseAdvancePayAPIUrl, NurseAdvanceReturnPayAPIUrl
} from '../utils/extensions/functionEx';
import { DATE_DISPLAY_FORMAT } from '../startups/apiConfig';
import NurseAdvancePayReturnForm from './nurseAdvancePayReturnForm';
import { useCookies } from 'react-cookie';

const NurseAdvancePaymentList = (props) => {
    const { expanded, formOpenMode, nurseId, handleAccordionChange, handleEditPayClick } = props;
    const { ShowAPIError, OpenErrorAlert, homeState, OpenSuccessAlert } = useContext(HomeContext);
    const { advancePayments, advanceReturnPayments, SetAdvancePayments, SetAdvanceReturnPayments, ClearLoggedUser } = useContext(NurseFormContext);

    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState(0);
    const [selectedPayTabIndex, setSelectedPayTabIndex] = React.useState(0);
    const [selectedPayment, setSelectedPayment] = React.useState(null);
    const [isOpenRetunForm, setIsOpenRetunForm] = React.useState(false);
    const [isDeleteReturnConfirmOpen, setIsDeleteReturnConfirmOpen] = React.useState(false);
    const [selectedReturnPayId, setSelectedReturnPayId] = React.useState(0);
    const [selectedReturnPay, setSelectedReturnPay] = React.useState(null);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        if (nurseId && nurseId > 0) {
            fetchNurseAdvancePayments();
            fetchNurseAdvanceReturnPayments();
        }
    }, [nurseId]);

    async function fetchNurseAdvancePayments() {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseAdvancePaySearchAPIUrl(homeState.loggedUser.companyCode, nurseId), { headers: headers });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                SetAdvancePayments(await response.json());
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
        }
    }

    async function fetchNurseAdvanceReturnPayments() {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseAdvanceReturnPaySearchAPIUrl(homeState.loggedUser.companyCode, nurseId), { headers: headers });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                SetAdvanceReturnPayments(await response.json());
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
        }
    }

    const handlePayTabChange = (event, newValue) => {
        setSelectedPayTabIndex(newValue);
    };

    const handleDeleteClick = (id) => {
        setSelectedId(id);
        setIsDeleteConfirmOpen(true);
    }

    const handleDeleteOKClick = async () => {
        try {
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseAdvancePayAPIUrl(homeState.loggedUser.companyCode), {
                method: "DELETE",
                headers: headers,
                body: JSON.stringify({ 'id': selectedId })
            });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Nurse advance payment successfully deleted.');
                await fetchNurseAdvancePayments();
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        } finally {
            setIsDeleteConfirmOpen(false);
            setSelectedId(0);
        }
    }

    const handleDeleteCancelClick = () => {
        setSelectedId(0);
        setIsDeleteConfirmOpen(false);
    }

    const handleEditClick = async (id) => {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseAdvancePayAPIUrl(homeState.loggedUser.companyCode, id), { headers: headers });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                const tempPayment = await response.json();
                handleEditPayClick(tempPayment);
            }
            else {
                ShowAPIError(response)
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        }
    }

    const handleAdvReturnClick = async (id) => {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseAdvancePayAPIUrl(homeState.loggedUser.companyCode, id), { headers: headers });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                setSelectedPayment(await response.json());
                setIsOpenRetunForm(true);
            }
            else {
                ShowAPIError(response);
                setSelectedPayment(null);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
            setSelectedPayment(null);
        }
    }

    const handleCloseAdvReturnClick = async (id) => {
        setSelectedPayment(null);
        setSelectedReturnPay(null);
        setIsOpenRetunForm(false);
    }

    const handleReturnPayDeleteClick = (id) => {
        setSelectedReturnPayId(id);
        setIsDeleteReturnConfirmOpen(true);
    }

    const handleReturnPayDeleteCancelClick = () => {
        setSelectedReturnPayId(0);
        setIsDeleteReturnConfirmOpen(false);
    }

    const handleReturnPayDeleteOKClick = async () => {
        try {
            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseAdvanceReturnPayAPIUrl(homeState.loggedUser.companyCode), {
                method: "DELETE",
                headers: headers,
                body: JSON.stringify({ 'id': selectedReturnPayId })
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Nurse advance return payment successfully deleted.');
                await fetchNurseAdvanceReturnPayments();
                await fetchNurseAdvancePayments();
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        } finally {
            setIsDeleteReturnConfirmOpen(false);
            setSelectedReturnPayId(0);
        }
    }

    const handleReturnPayEditClick = async (id) => {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            const response = await fetch(NurseAdvanceReturnPayAPIUrl(homeState.loggedUser.companyCode, id), { headers: headers });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                const tempReturnPayment = await response.json();
                setSelectedReturnPay(tempReturnPayment);

                if (tempReturnPayment.advance_pay_returns && tempReturnPayment.advance_pay_returns.length > 0) {
                    const responsePay = await fetch(NurseAdvancePayAPIUrl(homeState.loggedUser.companyCode,
                        tempReturnPayment.advance_pay_returns[0].nurse_advance_payment_id), { headers });
                    if (response.status === 401) {
                        OpenErrorAlert('User login history expired, please log in again.');
                        setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                        ClearLoggedUser();
                    } else if (responsePay.status === 200) {
                        const tempPay = await responsePay.json();
                        setSelectedPayment(tempPay);
                        setIsOpenRetunForm(true);
                    }
                    else {
                        ShowAPIError(responsePay);
                        setSelectedPayment(null);
                        setSelectedReturnPay(null);
                    }
                }
            }
            else {
                ShowAPIError(response)
            }
        }
        catch (error) {
            OpenErrorAlert(error)
        }
    }

    return (

        <Accordion expanded={expanded === 'panelNurseAdvancePayments'} onChange={handleAccordionChange('panelNurseAdvancePayments')}>
            <AccordionSummary id="panel-nurse-advance-payments">
                <Typography>Advance Payments</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Stack spacing={0}>
                    {
                        isOpenRetunForm && selectedPayment && selectedPayment.id > 0 &&
                        <NurseAdvancePayReturnForm isOpen={isOpenRetunForm} nurseId={nurseId} selectedPayment={selectedPayment}
                            selectedAdvReturnPayment={selectedReturnPay} handleClose={handleCloseAdvReturnClick} />
                    }
                    {
                        (isDeleteReturnConfirmOpen || isDeleteConfirmOpen) &&
                        <Box sx={{ pr: 1, float: 'right' }}>
                            <Stack direction="row" spacing={2}>
                                {
                                    isDeleteConfirmOpen &&
                                    <ConfirmDialog isOpen={isDeleteConfirmOpen} title="Confirm Delete"
                                        message="Are you sure you want to delete the advanvce payment?"
                                        handleClickOK={handleDeleteOKClick}
                                        handleClickCancel={handleDeleteCancelClick} />
                                }
                                {
                                    isDeleteReturnConfirmOpen &&
                                    <ConfirmDialog isOpen={isDeleteReturnConfirmOpen} title="Confirm Delete"
                                        message="Are you sure you want to delete the advanvce return payment?"
                                        handleClickOK={handleReturnPayDeleteOKClick}
                                        handleClickCancel={handleReturnPayDeleteCancelClick} />
                                }
                            </Stack>
                        </Box>
                    }

                    <Box sx={{ width: '100%' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={selectedPayTabIndex} onChange={handlePayTabChange}>
                                <Tab label="Advance Payments" {...a11yProps(0)} />
                                <Tab label="Advance Return Payments" {...a11yProps(1)} />
                            </Tabs>
                        </Box>
                        <TabPanel value={selectedPayTabIndex} index={0}>
                            <Stack spacing={1}>

                                <Grid container spacing={1}>
                                    {/* <Grid size={12}>
                                        
                                    </Grid> */}
                                    <Grid size={12}>
                                        <TableContainer component={Paper} sx={{ maxHeight: '77vh' }}>
                                            <Table stickyHeader sx={{ minWidth: 700 }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <StyledTableCell sx={{ width: '200px' }}>Pay Date</StyledTableCell>
                                                        <StyledTableCell sx={{ width: '200px' }}>Amount</StyledTableCell>
                                                        <StyledTableCell>Notes</StyledTableCell>
                                                        <StyledTableCell sx={{ width: '200px' }}>Returned Amount</StyledTableCell>
                                                        <StyledTableCell sx={{ width: '200px' }}>Balance Amount</StyledTableCell>
                                                        <StyledTableCell sx={{ width: '100px' }}>&nbsp;</StyledTableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {advancePayments.map((row) => (
                                                        <StyledTableRow key={row.id.toString()}>
                                                            <StyledTableCell>{GetDayJSValueFromUTCDate(row.pay_date).format(DATE_DISPLAY_FORMAT)}</StyledTableCell>
                                                            <StyledTableCell>{GetNoToCurrencyFormat(row.amount)}</StyledTableCell>
                                                            <StyledTableCell>{row.remarks}</StyledTableCell>
                                                            <StyledTableCell>{GetNoToCurrencyFormat(row.total_returned_amount ?? 0)}</StyledTableCell>
                                                            <StyledTableCell>{GetNoToCurrencyFormat(row.balance_amount ?? 0)}</StyledTableCell>
                                                            <StyledTableCell>
                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    sx={{
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    {
                                                                        formOpenMode === FormMode.ViewMode && row.balance_amount > 0 &&
                                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                                            <IconButton size="small" onClick={() => handleAdvReturnClick(row.id)}>
                                                                                <AssignmentReturnIcon fontSize="inherit" />
                                                                            </IconButton>
                                                                        </Stack>
                                                                    }
                                                                    {
                                                                        formOpenMode === FormMode.ViewMode && (row.total_returned_amount === 0 || row.balance_amount === 0) &&
                                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                                            <IconButton size="small" onClick={() => handleEditClick(row.id)}>
                                                                                <EditIcon fontSize="inherit" />
                                                                            </IconButton>
                                                                        </Stack>
                                                                    }
                                                                    {
                                                                        formOpenMode === FormMode.ViewMode && row.total_returned_amount === 0 &&
                                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                                            <IconButton size="small" color="error" onClick={() => handleDeleteClick(row.id)}>
                                                                                <RemoveCircleOutlineIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Stack>
                                                                    }</Stack>
                                                            </StyledTableCell>
                                                        </StyledTableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                </Grid>
                            </Stack>
                        </TabPanel>
                        <TabPanel value={selectedPayTabIndex} index={1}>
                            <Stack spacing={1}>
                                <Grid container spacing={1}>
                                    <Grid size={12}>
                                        <Box sx={{ pr: 1, float: 'right' }}>
                                            <Stack direction="row" spacing={2}>
                                                {
                                                    isDeleteConfirmOpen &&
                                                    <ConfirmDialog isOpen={isDeleteConfirmOpen} title="Confirm Delete"
                                                        message="Are you sure you want to delete the advanvce payment?"
                                                        handleClickOK={handleDeleteOKClick}
                                                        handleClickCancel={handleDeleteCancelClick} />
                                                }
                                            </Stack>
                                        </Box>
                                    </Grid>
                                    <Grid size={12}>
                                        <TableContainer component={Paper} sx={{ maxHeight: '77vh' }}>
                                            <Table stickyHeader sx={{ minWidth: 700 }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <StyledTableCell sx={{ width: '300px' }}>Pay Date</StyledTableCell>
                                                        <StyledTableCell sx={{ width: '350px' }}>Amount</StyledTableCell>
                                                        <StyledTableCell>Notes</StyledTableCell>
                                                        <StyledTableCell sx={{ width: '100px' }}>&nbsp;</StyledTableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {advanceReturnPayments.map((row) => (
                                                        <StyledTableRow key={row.id.toString()}>
                                                            <StyledTableCell>{GetDayJSValueFromUTCDate(row.pay_date).format(DATE_DISPLAY_FORMAT)}</StyledTableCell>
                                                            <StyledTableCell>{GetNoToCurrencyFormat(row.amount)}</StyledTableCell>
                                                            <StyledTableCell>{row.remarks}</StyledTableCell>
                                                            <StyledTableCell>

                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    sx={{
                                                                        justifyContent: "center",
                                                                        alignItems: "center",
                                                                    }}
                                                                >
                                                                    {
                                                                        formOpenMode === FormMode.ViewMode &&
                                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                                            <IconButton size="small" onClick={() => handleReturnPayEditClick(row.id)}>
                                                                                <EditIcon fontSize="inherit" />
                                                                            </IconButton>
                                                                        </Stack>
                                                                    }
                                                                    {
                                                                        formOpenMode === FormMode.ViewMode &&
                                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                                            <IconButton size="small" color="error" onClick={() => handleReturnPayDeleteClick(row.id)}>
                                                                                <RemoveCircleOutlineIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Stack>
                                                                    }</Stack>
                                                            </StyledTableCell>
                                                        </StyledTableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Grid>
                                </Grid>
                            </Stack>
                        </TabPanel>
                    </Box>
                </Stack>
            </AccordionDetails>
        </Accordion>

    );
}
export default NurseAdvancePaymentList;